import React, { useState } from 'react';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getApp } from 'firebase/app';

interface ChatWithSalesModalProps {
  show: boolean;
  onClose: () => void;
}

// Use existing Firebase app instance (initialized in LoginPage)
const app = getApp();
const db = getFirestore(app);

const ChatWithSalesModal: React.FC<ChatWithSalesModalProps> = ({ show, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    institutionName: '',
    message: '',
    urgency: 'normal'
  });

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      alert('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      // Save to Firestore - this will trigger Cloud Function to send email
      await addDoc(collection(db, 'chat_sales_requests'), {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        institutionName: formData.institutionName || 'Not provided',
        message: formData.message,
        urgency: formData.urgency,
        timestamp: serverTimestamp(),
        status: 'new'
      });

      setSubmitted(true);

      // Reset form after 2 seconds and close modal
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          institutionName: '',
          message: '',
          urgency: 'normal'
        });
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Sorry, there was an error sending your message. Please try again or contact us directly at contact@mylearningboard.com');
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        style={{ zIndex: 1050 }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header bg-primary-custom text-white">
              <h5 className="modal-title">
                <i className="bi bi-chat-dots me-2"></i>
                Chat with Sales Team
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onClose}
              ></button>
            </div>

            <div className="modal-body">
              {submitted ? (
                <div className="text-center py-5">
                  <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
                  <h4 className="mt-3 text-success">Message Sent Successfully!</h4>
                  <p className="text-muted">Our sales team will get back to you within 24 hours.</p>
                  <div className="mt-4">
                    <p className="small text-muted mb-2">Need immediate assistance?</p>
                    <div className="d-flex gap-2 justify-content-center">
                      <a href="tel:+918839393463" className="btn btn-sm btn-outline-primary">
                        <i className="bi bi-telephone me-1"></i>
                        Call Us
                      </a>
                      <a href="https://wa.me/918839393463" target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                        <i className="bi bi-whatsapp me-1"></i>
                        WhatsApp
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Info Banner */}
                  <div className="alert alert-info d-flex align-items-center mb-4">
                    <i className="bi bi-info-circle-fill me-2"></i>
                    <div className="small">
                      <strong>Quick Response Guaranteed!</strong> Our sales team typically responds within 2-4 hours during business hours.
                    </div>
                  </div>

                  <form onSubmit={handleSubmit}>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Your Name <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Enter your name"
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">Email Address <span className="text-danger">*</span></label>
                        <input
                          type="email"
                          className="form-control"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="your.email@example.com"
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">Phone Number <span className="text-danger">*</span></label>
                        <input
                          type="tel"
                          className="form-control"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          placeholder="+91 XXXXX XXXXX"
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">Institution Name</label>
                        <input
                          type="text"
                          className="form-control"
                          name="institutionName"
                          value={formData.institutionName}
                          onChange={handleChange}
                          placeholder="Your school/college name"
                        />
                      </div>

                      <div className="col-12">
                        <label className="form-label">Urgency Level</label>
                        <select
                          className="form-select"
                          name="urgency"
                          value={formData.urgency}
                          onChange={handleChange}
                        >
                          <option value="low">Low - General Inquiry</option>
                          <option value="normal">Normal - Interested in Learning More</option>
                          <option value="high">High - Ready to Purchase Soon</option>
                          <option value="urgent">Urgent - Need Immediate Assistance</option>
                        </select>
                      </div>

                      <div className="col-12">
                        <label className="form-label">Your Message <span className="text-danger">*</span></label>
                        <textarea
                          className="form-control"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows={4}
                          required
                          placeholder="Tell us what you need help with... e.g., pricing questions, feature inquiries, implementation details, etc."
                        ></textarea>
                        <div className="form-text">
                          <i className="bi bi-lightbulb me-1"></i>
                          Tip: Be specific about your requirements to get a more helpful response!
                        </div>
                      </div>
                    </div>

                    {/* Quick Topics */}
                    <div className="mt-3">
                      <label className="form-label small text-muted">Quick Topics (click to add to message):</label>
                      <div className="d-flex flex-wrap gap-2">
                        {[
                          'Pricing Information',
                          'Product Demo',
                          'Implementation Timeline',
                          'Feature Comparison',
                          'Integration Support',
                          'Training & Onboarding'
                        ].map((topic, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                message: formData.message
                                  ? `${formData.message}\n- ${topic}`
                                  : `I'd like to know about:\n- ${topic}`
                              });
                            }}
                          >
                            {topic}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="d-flex gap-2 mt-4">
                      <button type="submit" className="btn btn-primary-custom text-white flex-grow-1" disabled={submitting}>
                        {submitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Sending...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-send me-2"></i>
                            Send Message
                          </>
                        )}
                      </button>
                      <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>
                        Cancel
                      </button>
                    </div>
                  </form>

                  {/* Alternative Contact Methods */}
                  <div className="mt-4 pt-3 border-top">
                    <p className="small text-muted mb-2">Prefer other ways to reach us?</p>
                    <div className="d-flex gap-2 flex-wrap">
                      <a href="tel:+918839393463" className="btn btn-sm btn-outline-success">
                        <i className="bi bi-telephone me-1"></i>
                        +91 8839393463
                      </a>
                      <a href="mailto:contact@mylearningboard.com" className="btn btn-sm btn-outline-primary">
                        <i className="bi bi-envelope me-1"></i>
                        contact@mylearningboard.com
                      </a>
                      <a href="https://wa.me/918839393463" target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-success">
                        <i className="bi bi-whatsapp me-1"></i>
                        WhatsApp
                      </a>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatWithSalesModal;
